import React, { useEffect, useState } from "react";
import {
  Button,
  Layout,
  Menu,
  Dropdown,
  Form,
  theme,
  Grid,
  Tooltip,
  Spin,
} from "antd";
import {
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DownOutlined,
  InboxOutlined,
  DashboardOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  BarChartOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Profile from "../user/profile";
import RoleBasedAddOTP from "./RoleBasedAddOTP";
import Inbox from "../user/inbox";
import Stats from "../user/stats";
import LeaderboardV2 from "../superAdmin/leaderBoardV2";
import { useNavigate } from "react-router-dom";
import ChangePasswordModal from "../shared/ChangePasswordModal";
import eonLogoLight from "../../assets/images/eon-logo-light.png";
import "./RoleBasedTesterDashboard.css";

const { Header, Content, Footer } = Layout;
const { useBreakpoint } = Grid;
const dataApiUrl = process.env.REACT_APP_OTPCountPerApp;
const rolesApiUrl = process.env.REACT_APP_API_Roles;
const updatePassword = process.env.REACT_APP_API_UpdatePassword;
const roleBasedOTPStatsBase = process.env.REACT_APP_API_RoleBasedOTPStats;
const teamStatsURL = process.env.REACT_APP_GrapStats;
const userStatsURL = roleBasedOTPStatsBase
  ? `${roleBasedOTPStatsBase}/tester-opts-count-day-wise`
  : process.env.REACT_APP_testertats;

const NAV_ITEMS = [
  {
    key: "1",
    label: "Inbox",
    icon: <InboxOutlined />,
    component: Inbox,
    props: {
      smsinApiUrl: process.env.REACT_APP_API_RoleBasedSmsIn,
      variant: "roleBased",
    },
  },
  {
    key: "2",
    label: "Dashboard",
    icon: <DashboardOutlined />,
    component: Profile,
    props: {
      variant: "roleBased",
      ...(roleBasedOTPStatsBase
        ? {
            apiUrls: {
              dailyOTPCount: `${roleBasedOTPStatsBase}/otp-today`,
              monthlyOTPCount: `${roleBasedOTPStatsBase}/otp-current-month`,
              previousMonthOTPCount: `${roleBasedOTPStatsBase}/otp-last-month`,
              duplicateMonthlyCount: `${roleBasedOTPStatsBase}/duplicate-otp-current-month`,
            },
          }
        : {}),
    },
  },
  {
    key: "3",
    label: "Run Test",
    icon: <ThunderboltOutlined />,
    component: RoleBasedAddOTP,
    props: {},
  },
  {
    key: "4",
    label: "Team Statistics",
    icon: <TeamOutlined />,
    component: Stats,
    props: {
      title: "Total OTPs generated by the whole Team",
      apiUrl: teamStatsURL,
      userSpecific: false,
      variant: "roleBased",
      showLeaderboard: false,
    },
  },
  {
    key: "5",
    label: "Your Statistics",
    icon: <BarChartOutlined />,
    component: Stats,
    props: {
      title: "Your Statistics",
      apiUrl: userStatsURL,
      userSpecific: true,
      variant: "roleBased",
      showLeaderboard: false,
    },
  },
  {
    key: "6",
    label: "Leaderboard",
    icon: <TrophyOutlined />,
    component: LeaderboardV2,
    props: {},
  },
];

const HIDDEN_NAV_KEYS_FOR_SPECIAL_DB = ["4", "5", "6"];

const RoleBasedTesterDashboard = () => {
  const [selectedKey, setSelectedKey] = useState("1");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [filteredApps, setFilteredApps] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [form] = Form.useForm();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const roleId = localStorage.getItem("roleId") || user.roleId;
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const onLogout = () => {
    localStorage.removeItem("setupTime");
    localStorage.removeItem("user");
    localStorage.removeItem("roleId");
    navigate("/");
    window.location.reload();
  };

  const filterApps = (data) => {
    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    const filtered = ["ghana", "mali"].map((country) => {
      const apps = Object.entries(data[country] || {})
        .filter(([app, count]) => count > 70)
        .filter(([app]) => app !== "WhatsApp")
        .map(([app, count]) => ({ app, count }));
      return { country: capitalize(country), apps };
    });
    setFilteredApps(filtered);
  };

  const fetchChartData = async () => {
    try {
      const response = await fetch(dataApiUrl);
      if (!response.ok) throw new Error("Failed to fetch chart data");
      const data = await response.json();
      filterApps(data);
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await fetch(rolesApiUrl);
        if (!res.ok) {
          setRolesLoaded(true);
          return;
        }
        const json = await res.json();
        setRoles(json.data ?? json.body ?? []);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
      } finally {
        setRolesLoaded(true);
      }
    };
    fetchRoles();
  }, []);

  const role = roles.find((r) => Number(r.id) === Number(roleId));
  const isSpecialDbUser = role?.user_db === "special";
  const visibleNavItems = isSpecialDbUser
    ? NAV_ITEMS.filter((item) => !HIDDEN_NAV_KEYS_FOR_SPECIAL_DB.includes(item.key))
    : NAV_ITEMS;

  useEffect(() => {
    if (isSpecialDbUser && HIDDEN_NAV_KEYS_FOR_SPECIAL_DB.includes(selectedKey)) {
      setSelectedKey("1");
    }
  }, [isSpecialDbUser, selectedKey]);

  useEffect(() => {
    if (!isSpecialDbUser) fetchChartData();
  }, [isSpecialDbUser]);

  useEffect(() => {
    if (screens.md === false) setSidebarCollapsed(true);
  }, [screens.md]);

  const selectNavItem = (key) => {
    setSelectedKey(key);
    if (screens.md === false) setSidebarCollapsed(true);
  };

  const handlePasswordChange = () => setChangePasswordVisible(true);
  const handleCancel = () => setChangePasswordVisible(false);

  const handleFinish = async (values) => {
    try {
      const { currentPassword, newPassword } = values;
      const response = await axios.put(updatePassword, {
        userId: user.userId,
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      if (response.status === 200) {
        setChangePasswordVisible(false);
        onLogout();
      } else {
        console.error("Password change failed");
      }
    } catch (error) {
      console.error("Error changing password:", error);
    }
  };

  const menu = (
    <Menu>
      <Menu.Item
        key="changePassword"
        icon={<SettingOutlined />}
        onClick={handlePasswordChange}
      >
        Change Password
      </Menu.Item>
      <Menu.Item key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
        Logout
      </Menu.Item>
    </Menu>
  );

  const ActiveNav = visibleNavItems.find((item) => item.key === selectedKey);
  const ActiveComponent = ActiveNav?.component;
  const activeProps = { ...(ActiveNav?.props ?? {}), isSpecialDbUser };

  const appPillText =
    filteredApps.length > 0
      ? filteredApps
          .map(
            (c) =>
              `${c.country}: ${c.apps.map((a) => `${a.app} (${a.count})`).join(", ")}`,
          )
          .join(" • ")
      : "";

  if (!rolesLoaded) {
    return (
      <div
        className="rbt-v2-loading"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: colorBgContainer,
        }}
      >
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  return (
    <Layout
      className={`rbt-v2-layout ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
    >
      <aside
        className={`rbt-v2-sidebar ${sidebarCollapsed ? "collapsed" : ""}`}
      >
        <div className="rbt-v2-sidebar-header">
          {!sidebarCollapsed && (
            <div className="rbt-v2-logo">
              <img src={eonLogoLight} alt="EON Testing Tool" className="rbt-v2-logo-img" />
            </div>
          )}
          <Button
            type="text"
            icon={
              sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
            }
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="rbt-v2-collapse-btn"
          />
        </div>
        <nav className="rbt-v2-nav">
          {visibleNavItems.map((item) => (
            <Tooltip
              key={item.key}
              title={sidebarCollapsed ? item.label : null}
              placement="right"
            >
              <button
                className={`rbt-v2-nav-item ${selectedKey === item.key ? "active" : ""}`}
                onClick={() => selectNavItem(item.key)}
              >
                <span className="rbt-v2-nav-icon">{item.icon}</span>
                {!sidebarCollapsed && (
                  <span className="rbt-v2-nav-label">{item.label}</span>
                )}
              </button>
            </Tooltip>
          ))}
        </nav>
      </aside>

      <Layout className="rbt-v2-main">
        <Header className="rbt-v2-header">
          <div className="rbt-v2-header-left">
            <Button
              type="text"
              icon={
                sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
              }
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="rbt-v2-mobile-menu-btn"
              aria-label="Toggle sidebar"
            />
            <h1 className="rbt-v2-page-title">{ActiveNav?.label || "Inbox"}</h1>
          </div>
          {appPillText && !isSpecialDbUser && (
            <div className="rbt-v2-header-center">
              <Tooltip title={appPillText} placement="bottom">
                <span className="rbt-v2-app-pill">{appPillText}</span>
              </Tooltip>
            </div>
          )}
          <div className="rbt-v2-header-right">
            <Dropdown
              overlay={menu}
              trigger={["click"]}
              placement="bottomRight"
            >
              <button className="rbt-v2-user-btn" aria-label="Account menu">
                <span className="rbt-v2-user-avatar">
                  {user.name
                    ?.split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
                <span className="rbt-v2-user-name">
                  {user.name
                    ?.split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </span>
                <DownOutlined className="rbt-v2-user-chevron" />
              </button>
            </Dropdown>
          </div>
        </Header>

        <Content
          className="rbt-v2-content"
          style={{
            padding: screens.xs ? "16px" : "24px",
            background: colorBgContainer,
          }}
        >
          <div className="rbt-v2-content-inner">
            {ActiveComponent && <ActiveComponent {...activeProps} />}
          </div>
        </Content>

        <Footer className="rbt-v2-footer">
          EON Testing Tool ©{new Date().getFullYear()} Created by EON
          Intelligence
        </Footer>
      </Layout>

      <ChangePasswordModal
        open={changePasswordVisible}
        onCancel={handleCancel}
        onFinish={handleFinish}
        form={form}
      />
    </Layout>
  );
};

export default RoleBasedTesterDashboard;
